import { forwardRef, useEffect, useState } from "react";
import { format, differenceInMinutes } from "date-fns";
import { es } from "date-fns/locale";
import { QRLabelBase, LabelRow } from "./QRLabelBase";
import { api } from "@/lib/apiClient";
import { Skeleton } from "@/components/ui/skeleton";
import type { LabelSize, LaminationLabelData } from "@/types/labels";

interface PlybookExecution {
  id: string;
  work_order_id: string;
  plybook_id: string;
  status: string;
  total_plies: number;
  confirmed_plies: number;
  started_at?: string;
  completed_at?: string;
  operator_id?: string;
  operator?: { full_name: string };
  plybook?: {
    code: string;
    name: string;
  };
  work_order?: {
    order_number: string;
    mold?: { code: string };
    product_model?: { name: string };
    customer?: { legal_name: string };
  };
  plotter_cut?: {
    package_code: string;
    batch_number?: string;
    materials?: Array<{ material_name: string; layers: number; gsm?: number }>;
  };
  defect_count?: number;
}

interface LaminationLabelProps {
  plybookExecution?: PlybookExecution;
  plybookExecutionId?: string;
  shortCode?: string;
  size?: LabelSize;
}

export const LaminationLabel = forwardRef<HTMLDivElement, LaminationLabelProps>(
  ({ plybookExecution: propExecution, plybookExecutionId, shortCode, size = "medium" }, ref) => {
     
    const [execution, setExecution] = useState<PlybookExecution | null>(propExecution || null);
    const [loading, setLoading] = useState(!propExecution && !!plybookExecutionId);
    const [labelCode, setLabelCode] = useState(shortCode || "");

    useEffect(() => {
      if (propExecution) {
        setExecution(propExecution);
        return;
      }

      if (!plybookExecutionId) return;

      const fetchData = async () => {
        setLoading(true);
        try {
          // Fetch execution with related data
          const { data } = await api
            .from("plybook_executions")
            .select(`
              *,
              operator:profiles!plybook_executions_operator_id_fkey(full_name),
              plybook:plybooks(code, name),
              work_order:work_orders(
                order_number,
                mold:molds(code),
                product_model:product_models(name),
                customer:billing_customers(legal_name)
              )
            `)
            .eq("id", plybookExecutionId)
            .single();

          if (data) {
            // Get linked plotter cut if exists
            const { data: confirmation } = await api
              .from("plybook_ply_confirmations")
              .select("plotter_cut:plotter_cuts(package_code, batch_number, materials)")
              .eq("execution_id", plybookExecutionId)
              .not("plotter_cut_id", "is", null)
              .limit(1)
              .single();

            // Count defects
            const { count: defectCount } = await api
              .from("plybook_ply_confirmations")
              .select("*", { count: "exact", head: true })
              .eq("execution_id", plybookExecutionId)
              .eq("has_defect", true);

            setExecution({
              ...data,
              plotter_cut: confirmation?.plotter_cut,
              defect_count: defectCount || 0,
            });
          }

          // Get or create label code
          if (!shortCode) {
            const { data: existingLabel } = await api
              .from("label_prints")
              .select("short_code")
              .eq("plybook_execution_id", plybookExecutionId)
              .eq("label_type", "lamination")
              .eq("is_active", true)
              .order("printed_at", { ascending: false })
              .limit(1)
              .single();

            if (existingLabel) {
              setLabelCode(existingLabel.short_code);
            }
          }
        } catch (err) {
          console.error("Error fetching plybook execution:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, [plybookExecutionId, propExecution, shortCode]);

    if (loading) {
      return (
        <div style={{ width: size === "small" ? "50mm" : size === "medium" ? "100mm" : "150mm" }}>
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }

    if (!execution) {
      return <div className="text-destructive">Error: Ejecucion no encontrada</div>;
    }

    // Calculate duration
    const durationMinutes =
      execution.started_at && execution.completed_at
        ? differenceInMinutes(new Date(execution.completed_at), new Date(execution.started_at))
        : 0;

    // Build label data
    const labelData: LaminationLabelData = {
      // From plotter cut
      pkg: execution.plotter_cut?.package_code || "N/A",
      mats: (execution.plotter_cut?.materials || []).map((m) => ({
        n: m.material_name,
        l: m.layers,
        g: m.gsm,
      })),
      batch: execution.plotter_cut?.batch_number || "N/A",

      // Work order
      wo: execution.work_order?.order_number || "N/A",
      prod: execution.work_order?.product_model?.name || "N/A",
      mold: execution.work_order?.mold?.code || "N/A",

      // Lamination
      plybook: execution.plybook?.code || "N/A",
      plies: {
        total: execution.total_plies,
        done: execution.confirmed_plies,
        defects: execution.defect_count,
      },
      lam_op: execution.operator?.full_name || "N/A",
      lam_start: execution.started_at || "",
      lam_end: execution.completed_at || "",
      dur_m: durationMinutes,
      obs: undefined,
    };

    const displayCode = labelCode || `LAM-${execution.id.slice(-6).toUpperCase()}`;

    return (
      <QRLabelBase
        ref={ref}
        shortCode={displayCode}
        size={size}
        title={labelData.wo}
        subtitle={labelData.prod}
      >
        <div style={{ fontSize: "inherit" }}>
          {/* Mold and Plybook */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1mm" }}>
            <span>
              <strong>Molde:</strong> {labelData.mold}
            </span>
            <span>
              <strong>PB:</strong> {labelData.plybook}
            </span>
          </div>

          {/* Plies progress */}
          <div
            style={{
              backgroundColor: labelData.plies.done === labelData.plies.total ? "#d1fae5" : "#fef3c7",
              padding: "1mm",
              borderRadius: "1mm",
              marginBottom: "1mm",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              <strong>Capas:</strong> {labelData.plies.done}/{labelData.plies.total}
            </span>
            {labelData.plies.defects && labelData.plies.defects > 0 && (
              <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                {labelData.plies.defects} defectos
              </span>
            )}
          </div>

          {/* Source package */}
          {size !== "small" && (
            <LabelRow label="Paquete origen" value={labelData.pkg} />
          )}
          <LabelRow label="Lote" value={labelData.batch} highlight />

          {/* Timing */}
          {execution.completed_at && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1mm" }}>
              <span style={{ color: "#4a5568" }}>
                {format(new Date(execution.completed_at), "dd/MM/yy HH:mm", { locale: es })}
              </span>
              <span style={{ fontWeight: "bold" }}>
                {Math.floor(durationMinutes / 60)}h {durationMinutes % 60}m
              </span>
            </div>
          )}

          {/* Operator */}
          {size !== "small" && (
            <div
              style={{
                marginTop: "1mm",
                fontSize: "6pt",
                color: "#718096",
                textAlign: "right",
              }}
            >
              Op: {labelData.lam_op}
            </div>
          )}
        </div>
      </QRLabelBase>
    );
  }
);

LaminationLabel.displayName = "LaminationLabel";
